import { useCallback } from 'react'
import { formatNumberBySignificantFigures } from '../../utils/chartUtils'
import { deviceNames } from '../../utils/deviceDefinitions.js'
import { measurementDefinitions } from '../../utils/measurementDefinitions.js'
import { getDeviceBadgeClass, getFaultBadgeClass } from '../../utils/badgeUtils.js' // Assuming you create this file

const STAT_KEYS = ['min', 'max', 'avg', 'range']
const STAT_DISPLAY_NAMES = {
  min: 'Minimum',
  max: 'Maximum',
  avg: 'Average',
  range: 'Range',
}

function StatisticsDisplay({ statsData }) {

  const handleExportStats = useCallback(() => {
    if (!statsData || Object.keys(statsData).length === 0) {
      alert('No statistics data to export.')
      return
    }

    const headers = ['Device - Measurement', ...STAT_KEYS.map(key => STAT_DISPLAY_NAMES[key] || key)]
    const csvRows = [headers.join(',')]

    Object.entries(statsData).forEach(([seriesKey, seriesStats]) => {
      const row = [
        seriesKey.replace('_', ' - '),
        ...STAT_KEYS.map(statKey =>
          seriesStats[statKey] !== undefined
            ? formatNumberBySignificantFigures(seriesStats[statKey])
            : 'N/A',
        ),
      ]
      csvRows.push(row.join(','))
    })

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    if (link.download !== undefined) {
      const timestamp = new Date().toISOString().split('T')[0]
      link.href = URL.createObjectURL(blob)
      link.download = `statistics_export_${timestamp}.csv`
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    } else {
      alert('CSV export is not supported in your browser.')
    }
  }, [statsData])

  if (!statsData || Object.keys(statsData).length === 0) {
    return (
      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
          <h5 className="mb-0 fw-bold" id="statistics-heading">
            <i className="bi bi-bar-chart-line-fill me-2" aria-hidden="true"></i>Statistics
          </h5>
          <button className="btn btn-sm btn-outline-secondary" disabled>
            <i className="bi bi-download me-1" aria-hidden="true"></i>Download Stats
          </button>
        </div>
        <div className="alert alert-light mb-0" role="status">No statistics data to display. Select data.</div>
      </div>
    )
  }

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
        <h5 className="mb-0 fw-bold" id="statistics-heading">
          <i className="bi bi-bar-chart-line-fill me-2" aria-hidden="true"></i>Statistics for Selected Data
        </h5>
        <button className="btn btn-sm btn-outline-secondary" onClick={handleExportStats}>
          <i className="bi bi-download me-1" aria-hidden="true"></i>Download Stats
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover align-middle mb-0" aria-labelledby="statistics-heading">
          <thead className="thead-light">
            <tr>
              <th scope="col">Device - Measurement</th>
              {STAT_KEYS.map(key => (
                <th key={key} scope="col" className="text-end">{STAT_DISPLAY_NAMES[key] || (key.charAt(0).toUpperCase() + key.slice(1))}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(statsData).map(([seriesKey, seriesStats]) => {
              const parts = seriesKey.split(/[-_]/)
              const device = parts[0]
              const measurement = parts.slice(1).join('-')
              return (
                <tr key={seriesKey}>
                  <th scope="row">
                    <span className={`badge ${getDeviceBadgeClass(device)} me-1`}>{deviceNames[device] || device}</span>
                    <span className={`badge ${getFaultBadgeClass(measurement)}`}>{measurementDefinitions[measurement]?.friendlyName || measurement}</span>
                  </th>
                  {STAT_KEYS.map(statKey => (
                    <td key={statKey} className="text-end">
                      {seriesStats[statKey] !== undefined
                       ? formatNumberBySignificantFigures(seriesStats[statKey])
                       : 'N/A'}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default StatisticsDisplay
